import React, { useRef, useState, useCallback, useEffect } from 'react';
import {Icon } from '@rsuite/icons';

import DoublePanel from "../../../components/panels/doublePanel/doublePanel";
import BasicDropdown from '../../../components/inputs/basicDropdown/basicDropdown';
import BasicCheckbox from '../../../components/basicCheckbox/basicCheckbox';
import BasicRadioGroup from '../../../components/basicRadioGroup/basicRadioGroup';
import BasicInputDropdown from '../../../components/inputs/basicInputDropdown/basicInputDropdown';

import PlacesPanel from '../../../components/panels/placesPanel/placesPanel';

import { getContractsByID, getTemplatesByContract, getUserInfoByContract } from '../../../api/user';
import { getDefaultInvoiceDataWithZeros, useInvoiceContext } from '../createInvoiceContext';
import { getFrontendBuildInfo } from '../../../utils/cargoNameValidationDiagnostics';
import { getPopularCities, getSuggestedCities, getCityInfo } from '../../../api/logistic';
import { getCurrencyRate } from '../../../api/currency';
import applyTemplate from './utils/applyTemplate';
import { debounce } from 'lodash';
import {formatPhoneNumber} from "./utils/phoneFormatter";
import BasicInput from '../../../components/inputs/basicInput/basicInput';

import { useMessenger } from "../../../context/messengerContext";
import { cutText } from '../../../utils/format';
import { useToaster, Message } from 'rsuite';
import { shouldApplyEAEULogic, getEAEUDefaultValues, isPaymentOnReceiptAllowed } from '../../../utils/eaeuHelper';
import EAEUWarningModal from '../../../components/eaeuWarningModal/eaeuWarningModal';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';

const getVatValues = (t) => [
    { label: t("no_vat"), value: -1 },
    { label: "0%", value: 0 },
    { label: "10%", value: 10 },
    { label: "12%", value: 12 },
    { label: "20%", value: 20 },
]

const getUserTypeValues = (t) => [
    {label: t('sender'), value: 'sender'},
    {label: t('receiver'), value: 'reciver'},
    {label: t('third_party'), value: 'another'},
]

const vatMultiplierToPercent = (vatMultiplierRaw) => {
    const n = Number(vatMultiplierRaw);
    if (!Number.isFinite(n)) return null;

    // Backend sends VAT as multiplier:
    // 1    -> 0%
    // 1.12 -> 12%
    // 1.2  -> 20%
    if (n <= 1) return 0;

    const percent = Math.round((n - 1) * 100);
    return percent < 0 ? 0 : percent;
}

const SECOND_PANEL_HEADER_SVG =
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.625 17.5C16.6605 17.5 17.5 16.6605 17.5 15.625C17.5 14.5895 16.6605 13.75 15.625 13.75C14.5895 13.75 13.75 14.5895 13.75 15.625C13.75 16.6605 14.5895 17.5 15.625 17.5Z" stroke="url(#paint0_linear_8010_626)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.625 4.375H13.125C13.788 4.375 14.4239 4.63839 14.8928 5.10723C15.3616 5.57607 15.625 6.21196 15.625 6.875C15.625 7.53804 15.3616 8.17393 14.8928 8.64277C14.4239 9.11161 13.788 9.375 13.125 9.375H5.625C4.7962 9.375 4.00134 9.70424 3.41529 10.2903C2.82924 10.8763 2.5 11.6712 2.5 12.5C2.5 13.3288 2.82924 14.1237 3.41529 14.7097C4.00134 15.2958 4.7962 15.625 5.625 15.625H13.75" stroke="url(#paint1_linear_8010_626)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <defs>
        <linearGradient id="paint0_linear_8010_626" x1="13.75" y1="15.625" x2="17.5" y2="15.625" gradientUnits="userSpaceOnUse">
        <stop stop-color="#55E5FF"/>
        <stop offset="1" stop-color="#5DFF66"/>
        </linearGradient>
        <linearGradient id="paint1_linear_8010_626" x1="2.5" y1="10" x2="15.625" y2="10" gradientUnits="userSpaceOnUse">
        <stop stop-color="#55E5FF"/>
        <stop offset="1" stop-color="#5DFF66"/>
        </linearGradient>
        </defs>
    </svg>

const MOCK_CITY_DATA = [
    {
        label: "Москва",
        value: 1
    },
    {
        label: "Санкт-Петербург",
        value: 2
    }
]


export default function InvoiceMain({isTemplate, isCalculate, isTemplateUpdate}) {
    const { t } = useTranslation('basic');
    const [contracts, setContracts] = useState([]);

    const [cityFromLoading, setCityFromLoading] = useState(false);
    const [cityToLoading, setCityToLoading] = useState(false);

    const [popularCities, setPopularCities] = useState([]);

    const [searchQueryFrom, setSearchQueryFrom] = useState('');
    const [searchedCitiesFrom, setSearchedCitiesFrom] = useState([]);

    const [searchQueryTo, setSearchQueryTo] = useState('');
    const [searchedCitiesTo, setSearchedCitiesTo] = useState([]);

    const { invoiceData, setInvoiceData, templates, setTemplates, cache, setCache } = useInvoiceContext();

    const [updateUserInfo, setUpdateUserInfo] = useState(0);
    
    const { userId, platform } = useMessenger();

    const fromCityRef = useRef(null);
    const toCityRef = useRef(null);

    const { template_id: templateIdFromParams } = useParams();
    const searchParams = new URLSearchParams(window.location.search);
    const template_id = templateIdFromParams ?? searchParams.get('template_id');

    const toaster = useToaster();
    const [contractWarningShown, setContractWarningShown] = useState(false);

    // EAEU logic state
    const [showEAEUWarning, setShowEAEUWarning] = useState(false);
    const [userTypeDropdownOpen, setUserTypeDropdownOpen] = useState(false);

    useEffect(() => {
        console.log(invoiceData.payment_on_receipt);
        if (invoiceData.payment_on_receipt) return;

        // Reset all values as well as all places.goods.[x].price to null
        setInvoiceData(oldData => {
            return {...oldData, additional_sum: 0, commision: false, delivery_pay: false, hand_sum: 0, places: 
                oldData.places.map((place) => {
                    return {...place, goods: place.goods.map((good) => {
                        return {...good, price: null};
                    })};
                })
            };
        })
    }, [invoiceData.payment_on_receipt])

    // Apply template if it's template update 
    useEffect(() => {
        if (!isTemplateUpdate) return;
        if (!template_id) return;
        if (!templates.length) return;

        const template = templates.find((t) => t.id == template_id);

        if (!template) return;
        if (invoiceData.lastAppliedTemplateId === template.id) return;

        let data_to_merge = applyTemplate({invoiceData, templateData: template});

        // Set flag to prevent autocorrection from resetting address fields during template application
        setInvoiceData(oldData => {
            return {...oldData, ...data_to_merge, template_name: template.template_name, template_id: template.id, lastAppliedTemplateId: template.id, _isApplyingTemplate: true};
        });
        
        // Clear the flag after a short delay to allow normal autocorrection for user changes
        setTimeout(() => {
            setInvoiceData(currentData => {
                const {_isApplyingTemplate, ...cleanData} = currentData;
                return cleanData;
            });
        }, 100);
    }, [templates])

    /* Refocus inputs on citysearch 
    useEffect(() => {
        if (!searchQueryFrom.length) return;
        if (fromCityRef.current && fromCityRef.current.target) {
            console.log(fromCityRef.current);
            let input_node = fromCityRef.current.target?.querySelector('input');
            
            //console.log(input_node);
            //if (!input_node.value) return;

            console.log(input_node);

            setTimeout(() => input_node.click(), 1000);
        }
    }, [searchedCitiesFrom]);

    useEffect(() => {
        if (!searchQueryTo.length) return;
        if (toCityRef.current && toCityRef.current.target) {
            let input_node = toCityRef.current.target?.querySelector('input');

            console.log(input_node);

            setTimeout(() => input_node.click(), 1000);
        }
    }, [searchedCitiesTo]);
    */

    // debounce with support of abort controller
    const getCitiesDebouncedFrom = useCallback(debounce((query, contract_number) => {
        if (!contract_number) return;

        setCityFromLoading(true);

        getSuggestedCities({contract_number, query}).then((res) => {
            setCityFromLoading(false);

            if (res) {
                setSearchedCitiesFrom(res);

                // Cache the result to avoid future delays
                setCache(prev => ({
                    ...prev,
                    [`${contract_number}_${query.toLowerCase()}`]: res
                }));
            }
        });
    }, 500), [setCache]);

    const getCitiesDebouncedTo = useCallback(debounce((query, contract_number) => {
        if (!contract_number) return;

        setCityToLoading(true);

        getSuggestedCities({contract_number, query}).then((res) => {
            setCityToLoading(false);

            if (res) {
                setSearchedCitiesTo(res);

                // Cache the result to avoid future delays
                setCache(prev => ({
                    ...prev,
                    [`${contract_number}_${query.toLowerCase()}`]: res
                }));
            }
        });
    }, 500), [setCache]);

    // set auto subject
    useEffect(() => {
        if (!invoiceData.contract_number) return;
        if (!invoiceData.user_info) return;

        let subject = null;
        let targetRole = null;

        if (invoiceData.user_type == "sender") {
            subject = invoiceData.delivery.sender;
            targetRole = "sender";
        } else if (invoiceData.user_type == "reciver") {
            subject = invoiceData.delivery.recipient;
            targetRole = "recipient";
        }

        if (!subject || Object.keys(subject).length == 0) return;

        // Check if any field needs to be filled
        const needsFio = !subject.fio;
        const needsContractFace = !subject.contract_face;
        const needsPhone = !subject.phones?.[0]?.number;

        // Only update if at least one field needs to be filled
        if (!needsFio && !needsContractFace && !needsPhone) return;

        // Prepare the new values
        const newFio = needsFio 
            ? (invoiceData.user_info.client_company_name || `${invoiceData.user_info.first_name} ${invoiceData.user_info.last_name} ${invoiceData.user_info.middle_name}`)
            : subject.fio;
        const newContractFace = needsContractFace 
            ? `${invoiceData.user_info.first_name} ${invoiceData.user_info.last_name} ${invoiceData.user_info.middle_name}`
            : subject.contract_face;
        const newPhoneNumber = needsPhone 
            ? formatPhoneNumber(invoiceData.user_info.phone_number, 'Россия')
            : subject.phones[0].number;

        if (needsPhone) {
            console.log(invoiceData.user_info.phone_number);
            console.log(newPhoneNumber);
        }

        // Create deep copy and update atomically
        setInvoiceData((oldData) => {
            const currentSubject = targetRole === "sender" ? oldData.delivery.sender : oldData.delivery.recipient;
            
            // Deep copy the subject with updated values
            const updatedSubject = {
                ...currentSubject,
                fio: needsFio ? newFio : currentSubject.fio,
                contract_face: needsContractFace ? newContractFace : currentSubject.contract_face,
                phones: currentSubject.phones.map((phone, idx) => 
                    idx === 0 && needsPhone 
                        ? { ...phone, number: newPhoneNumber }
                        : { ...phone }
                )
            };

            // Deep copy the delivery object
            return {
                ...oldData,
                delivery: {
                    ...oldData.delivery,
                    [targetRole]: updatedSubject
                }
            };
        });

        const contractVatPercent = invoiceData.contract_vat_percent ?? invoiceData.user_info?.contract_vat_percent;
        if (contractVatPercent !== null && contractVatPercent !== undefined) {
            setInvoiceData((oldData) => {
                // Deep copy places array
                const newPlaces = oldData.places.map((place) => ({
                    ...place,
                    goods: place.goods.map((good) => ({
                        ...good,
                        vat: contractVatPercent,
                        vat_sum: (contractVatPercent <= 0 || good.price === null || good.price === undefined) ? 0 :
                            Math.round((good.price - (good.price / (contractVatPercent / 100.0 + 1))) * 100) / 100
                    }))
                }));

                return {
                    ...oldData,
                    vat: contractVatPercent,
                    places: newPlaces
                };
            });
        }
    }, [invoiceData.user_type, updateUserInfo])

    useEffect(() => {
        console.log("SAS")
        if (!invoiceData.recaluclatePlaces) return;

        setInvoiceData((oldData) => {
            const newData = {...oldData};
            let p = [];

            newData.places.map((place, place_index) => {
                let pl = {
                    number: place_index + 1,
                    height: place.height || 0,
                    width: place.width || 0,
                    length: place.length || 0,
                    weight: place.weight || 0,
                    goods: place.goods || [{
                        name: t('default_goods_name'),
                        weight: 0,
                        count: 1,
                        article: "",
                        price: null,
                        insurance: 0,
                        vat: -1
                    }]
                };
                console.log(place?.goods?.reduce((acc, good) => acc + good.weight, 0))
                pl = {...pl, weight: pl.weight || place?.goods?.reduce((acc, good) => acc + good.weight, 0)};

                console.log(pl);
                p.push(pl);
            });

            newData.places = p;
            newData.recaluclatePlaces = false;
            return newData;
        });
    }, [invoiceData.recaluclatePlaces])

    // === Getters ===

    // Contract
    useEffect(() => {
        if (!userId) return;
        getContractsByID({ platform, userId }).then((res) => {
            if (res) {
                setContracts(res.map((contract_number) => ({label: contract_number, value: contract_number})));
                fetch(`${process.env.REACT_APP_API_URL}/api/debug/log`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({ ...getFrontendBuildInfo(), platform, userId: userId ?? null, contracts_shown: res })
                }).catch(() => {});
            }
        });
    }, [userId])

    useEffect(() => {
        if (contracts.length === 0) return;
        if (isTemplateUpdate) {
            // get contract number from get parametr
            const contract_number = searchParams.get('contract_number');
            if (contract_number && contracts.some(c => c.value === contract_number)) {
                setInvoiceData((oldData) => {
                    const newData = {...oldData};
                    newData.contract_number = contract_number;
                    return newData;
                })
            }
        }
        if (contracts.length === 1) {
            setInvoiceData((oldData) => {
                const newData = {...oldData};
                newData.contract_number = contracts[0].value;
                return newData;
            })
        }
    }, [contracts])

    // Templates
    useEffect(() => {
        if (!invoiceData.contract_number) return;
        getTemplatesByContract({contract_number: invoiceData.contract_number}).then((res) => {
            if (res) {
                setTemplates(res);
            }
        });
    }, [invoiceData.contract_number])

    // User info
    useEffect(() => {
        if (!invoiceData.contract_number) return;
        getUserInfoByContract({contract_number: invoiceData.contract_number}).then(async (res) => {
            if (res) {
                let currencyRate = 1.0;
                if (res.is_kz) {
                    try {
                        const rate = await getCurrencyRate('KZT');
                        currencyRate = rate.value;
                    } catch (e) {
                        console.error('Failed to load KZT rate', e);
                        currencyRate = 6.0;
                    }
                }
                const vatMultiplier = res.contract_vat ?? res.vat;
                const contractVatPercent = vatMultiplierToPercent(vatMultiplier);

                console.log('contractVatPercent', contractVatPercent);

                setInvoiceData((prev) => ({
                    ...prev,
                    user_info: {
                        ...res,
                        contract_vat: res.contract_vat ?? res.vat,
                        contract_vat_percent: contractVatPercent,
                    },
                    contract_vat_percent: contractVatPercent,
                    currencyRate
                }));
                setUpdateUserInfo(updateUserInfo => updateUserInfo + 1);
            }
        });
    }, [invoiceData.contract_number])

    // Cities popular cities
    useEffect(() => {
        setCityFromLoading(true);
        setCityToLoading(true);

        getPopularCities().then((res) => {
            setCityFromLoading(false);
            setCityToLoading(false);

            if (res) {
                setPopularCities(res);
            }
        });
    }, []);

    // Search for cities from with debounce
    useEffect(() => {
        if (searchQueryFrom.length < 3) return;

        if (!invoiceData.contract_number) {
            if (!contractWarningShown) {
                toaster.push(<Message type="error" showIcon>{t("select_contract_for_city_search")}</Message>, { placement: "topCenter", duration: 3000 });
                setContractWarningShown(true);
            }
            return;
        } else {
            if (contractWarningShown) {
                setContractWarningShown(false);
            }
        }

        const cacheKey = `${invoiceData.contract_number}_${searchQueryFrom.toLowerCase()}`;

        if (cache[cacheKey]) {
            setSearchedCitiesFrom(cache[cacheKey]);
        } else {
            getCitiesDebouncedFrom(searchQueryFrom, invoiceData.contract_number);
        }
    }, [searchQueryFrom, invoiceData.contract_number, getCitiesDebouncedFrom, cache]);

    // Search for cities to
    useEffect(() => {
        if (searchQueryTo.length < 3) return;

        if (!invoiceData.contract_number) {
            if (!contractWarningShown) {
                toaster.push(<Message type="error" showIcon>{t("select_contract_for_city_search")}</Message>, { placement: "topCenter", duration: 3000 });
                setContractWarningShown(true);
            }
            return;
        } else {
            if (contractWarningShown) {
                setContractWarningShown(false);
            }
        }

        const cacheKeyTo = `${invoiceData.contract_number}_${searchQueryTo.toLowerCase()}`;

        if (cache[cacheKeyTo]) {
            setSearchedCitiesTo(cache[cacheKeyTo]);
        } else {
            getCitiesDebouncedTo(searchQueryTo, invoiceData.contract_number);
        }
    }, [searchQueryTo, invoiceData.contract_number, getCitiesDebouncedTo, cache]);

    // Apply template on select
    useEffect(() => {
        if (!invoiceData.template_id) return;

        if (invoiceData.template_id === invoiceData.lastAppliedTemplateId) return;

        if (invoiceData.template_id == -1) {
            setInvoiceData({...getDefaultInvoiceDataWithZeros(t('default_goods_name')), template_id: null, lastAppliedTemplateId: null, contract_number: invoiceData.contract_number, delivery: invoiceData.delivery, user_info: invoiceData.user_info});
            return;

            console.log("pretemplate_invoice_data");

            let previous_data = localStorage.getItem('pretemplate_invoice_data');

            if (previous_data) {
                setInvoiceData({...JSON.parse(previous_data), template_id: null});
                localStorage.removeItem('pretemplate_invoice_data');
                setSearchQueryFrom('');
                setSearchQueryTo('');
            }

            return;
        }

        localStorage.setItem('pretemplate_invoice_data', JSON.stringify(invoiceData));

        let data_to_merge = applyTemplate({invoiceData, templateData: templates.find((t) => t.id === invoiceData.template_id)});
        setTimeout(async () => {
            // Set flag to prevent autocorrection from resetting address fields during template application
            const updatedData = {...invoiceData, ...data_to_merge, _isApplyingTemplate: true};
            
            // If cities are being applied from template, fetch their country codes
            const promises = [];
            if (data_to_merge?.cityFrom && invoiceData.contract_number) {
                promises.push(
                    getCityInfo({contract_number: invoiceData.contract_number, code: data_to_merge.cityFrom})
                        .then((info) => {
                            if (info) {
                                return {
                                    cityFromLabel: info.label || info.name || '',
                                    cityFromCountryCode: info.countryCode ?? 1
                                };
                            }
                            return {};
                        })
                        .catch(() => ({}))
                );
            }
            
            if (data_to_merge?.cityTo && invoiceData.contract_number) {
                promises.push(
                    getCityInfo({contract_number: invoiceData.contract_number, code: data_to_merge.cityTo})
                        .then((info) => {
                            if (info) {
                                return {
                                    cityToLabel: info.label || info.name || '',
                                    cityToCountryCode: info.countryCode ?? 1
                                };
                            }
                            return {};
                        })
                        .catch(() => ({}))
                );
            }
            
            // Wait for city info fetching to complete
            if (promises.length > 0) {
                const cityInfoResults = await Promise.all(promises);
                const cityInfoData = cityInfoResults.reduce((acc, result) => ({...acc, ...result}), {});
                const finalData = {...updatedData, ...cityInfoData, lastAppliedTemplateId: invoiceData.template_id};
                
                // Block payment on receipt if it's not allowed for current route
                const isAllowed = isPaymentOnReceiptAllowed(finalData.cityFromCountryCode, finalData.cityToCountryCode, finalData.user_info?.is_kz);
                if (!isAllowed) {
                    finalData.payment_on_receipt = false;
                }
                
                // Now format phone numbers with correct country codes
                if (finalData.delivery) {
                    if (finalData.delivery.sender?.phones?.length > 0) {
                        const senderCountryCode = finalData.cityFromCountryCode || 1;
                        finalData.delivery.sender.phones = finalData.delivery.sender.phones.map(phone => ({
                            ...phone,
                            number: formatPhoneNumber(phone.number, senderCountryCode)
                        }));
                    }
                    
                    if (finalData.delivery.recipient?.phones?.length > 0) {
                        const recipientCountryCode = finalData.cityToCountryCode || 1;
                        finalData.delivery.recipient.phones = finalData.delivery.recipient.phones.map(phone => ({
                            ...phone,
                            number: formatPhoneNumber(phone.number, recipientCountryCode)
                        }));
                    }
                }
                
                setInvoiceData(finalData);
            } else {
                setInvoiceData({...updatedData, lastAppliedTemplateId: invoiceData.template_id});
            }

            // Clear the flag after a short delay to allow normal autocorrection for user changes
            setTimeout(() => {
                setInvoiceData(currentData => {
                    const {_isApplyingTemplate, ...cleanData} = currentData;
                    return cleanData;
                });
            }, 100);
        }, 500);

    }, [invoiceData.template_id])

    useEffect(() => {
        if (!invoiceData.cityFromMakeSearch) return;

        setInvoiceData((oldData) => {
            console.log("Try")
            if (searchedCitiesFrom.length < 1) {
                setTimeout(() => setInvoiceData(oldData => {console.log("Trying..."); return {...oldData, cityFromMakeSearch: true} }), 2000);
                
                setCityFromLoading(true);
                return {...oldData, cityFromMakeSearch: false};
            };
            console.log("Retrying")

            const newData = {...oldData};

            console.log(searchedCitiesFrom[0]);
            newData.cityFrom = searchedCitiesFrom[0].value;
            newData.cityFromMakeSearch = false;

            setCityFromLoading(false);

            return newData
        })
    }, [invoiceData.cityFromMakeSearch])

    useEffect(() => {
        if (!invoiceData.cityToMakeSearch) return;

        setInvoiceData((oldData) => {
            console.log("Try")
            if (searchedCitiesTo.length < 1) {
                setTimeout(() => setInvoiceData(oldData => {console.log("Trying..."); return {...oldData, cityToMakeSearch: true} }), 2000);
                
                setCityToLoading(true);
                return {...oldData, cityToMakeSearch: false};
            };
            console.log("Retrying")

            const newData = {...oldData};

            console.log(searchedCitiesTo[0]);
            newData.cityTo = searchedCitiesTo[0].value;
            newData.cityToMakeSearch = false;

            setCityToLoading(false);

            return newData
        })
    }, [invoiceData.cityToMakeSearch])

    // Apply city on template select
    useEffect(() => {
        if (!invoiceData.cityFromLabel) return;

        let trancatedCountry = invoiceData.cityFromLabel;

        if (invoiceData.cityFromLabel.startsWith("Россия")) {
            trancatedCountry = invoiceData.cityFromLabel.replace("Россия, ", "");
        }

        if (invoiceData.cityFromLabel.startsWith("Беларусь")) {
            trancatedCountry = invoiceData.cityFromLabel.replace("Беларусь, ", "");
        }

        if (invoiceData.cityFromLabel.startsWith("Казахстан")) {
            trancatedCountry = invoiceData.cityFromLabel.replace("Казахстан, ", "");
        }

        if (invoiceData.cityFromLabel.startsWith("Киргизия")) {
            trancatedCountry = invoiceData.cityFromLabel.replace("Киргизия, ", "");
        }

        if (invoiceData.cityFromLabel.startsWith("Армения")) {
            trancatedCountry = invoiceData.cityFromLabel.replace("Армения, ", "");
        }

        setSearchQueryFrom(trancatedCountry.split(",").slice(-1)[0].split(" ").slice(-1)[0]);
    }, [invoiceData.cityFromLabel])

    useEffect(() => {
        if (!invoiceData.cityToLabel) return;

        let trancatedCountry = invoiceData.cityToLabel;

        if (invoiceData.cityToLabel.startsWith("Россия")) {
            trancatedCountry = invoiceData.cityToLabel.replace("Россия, ", "");
        }

        if (invoiceData.cityToLabel.startsWith("Беларусь")) {
            trancatedCountry = invoiceData.cityToLabel.replace("Беларусь, ", "");
        }

        if (invoiceData.cityToLabel.startsWith("Казахстан")) {
            trancatedCountry = invoiceData.cityToLabel.replace("Казахстан, ", "");
        }

        if (invoiceData.cityToLabel.startsWith("Киргизия")) {
            trancatedCountry = invoiceData.cityToLabel.replace("Киргизия, ", "");
        }

        if (invoiceData.cityToLabel.startsWith("Армения")) {
            trancatedCountry = invoiceData.cityToLabel.replace("Армения, ", "");
        }

        setSearchQueryTo(trancatedCountry.split(",").slice(-1)[0].split(" ").slice(-1)[0]);
    }, [invoiceData.cityToLabel])

    useEffect(() => {
        if (!invoiceData.cityFrom) return;

        let subject = invoiceData.user_type == 'sender' ? 'sender' : invoiceData.user_type == 'reciver' ? 'recipient' : '';

        if (!subject) return;

        if (invoiceData.delivery?.[subject]?.phones?.length > 0) {
            let n = invoiceData.delivery?.[subject]?.phones[0]?.number;
            let shouldClear = false;

            if (invoiceData.cityFromLabel?.startsWith("Россия") && !n.startsWith("+7")) {
                shouldClear = true;
            } else if (invoiceData.cityFromLabel?.startsWith("Беларусь") && !n.startsWith("+375")) {
                shouldClear = true;
            } else if (invoiceData.cityFromLabel?.startsWith("Казахстан") && !n.startsWith("+7")) {
                shouldClear = true;
            } else if (invoiceData.cityFromLabel?.startsWith("Киргизия") && !n.startsWith("+996")) {
                shouldClear = true;
            } else if (invoiceData.cityFromLabel?.startsWith("Армения") && !n.startsWith("+374")) {
                shouldClear = true;
            }

            if (shouldClear) {
                setInvoiceData(oldData => ({
                    ...oldData,
                    delivery: {
                        ...oldData.delivery,
                        [subject]: {
                            ...oldData.delivery[subject],
                            phones: oldData.delivery[subject].phones.map((phone, idx) => 
                                idx === 0 ? {...phone, number: ""} : phone
                            )
                        }
                    }
                }));
            }
        }

    }, [invoiceData.cityFrom]);

    useEffect(() => {
        // Block payment on receipt if it's not allowed for current route
        // Wait for user_info to be loaded before checking
        if (!invoiceData.user_info) return;
        
        const isAllowed = isPaymentOnReceiptAllowed(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData.user_info?.is_kz);
        
        console.log('Payment check:', {
            from: invoiceData.cityFromCountryCode,
            to: invoiceData.cityToCountryCode,
            isKz: invoiceData.user_info?.is_kz,
            isAllowed,
            currentValue: invoiceData.payment_on_receipt
        });
        
        if (invoiceData.cityFromCountryCode !== undefined && invoiceData.cityToCountryCode !== undefined) {
            // Only set to false if not allowed AND current value is true
            // This prevents unnecessary re-renders and conflicts with user actions
            if (!isAllowed && invoiceData.payment_on_receipt === true) {
                setInvoiceData(oldData => {
                    let newData = {...oldData};
                    newData.payment_on_receipt = false;
                    return newData
                })
            }
        }
    }, [invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData.user_info?.is_kz]);


    useEffect(() => {
        if (!invoiceData.cityTo) return;

        // If we already have a country code (likely set from getCityInfo), do not override unless we have new data.
        const cityDetails = searchedCitiesTo.find((city) => city.value === invoiceData.cityTo);

        if (!cityDetails || cityDetails.countryCode === undefined) return;

        setInvoiceData(oldData => ({
            ...oldData,
            cityToCountryCode: cityDetails.countryCode
        }));
    }, [invoiceData.cityTo, searchedCitiesTo]);

    useEffect(() => {
        if (!invoiceData.cityFrom) return;

        const cityDetails = searchedCitiesFrom.find((city) => city.value === invoiceData.cityFrom);

        if (!cityDetails || cityDetails.countryCode === undefined) return;

        setInvoiceData(oldData => ({
            ...oldData,
            cityFromCountryCode: cityDetails.countryCode
        }));
    }, [invoiceData.cityFrom, searchedCitiesFrom]);

    useEffect(() => {
        if (!invoiceData.cityTo) return;

        let subject = invoiceData.user_type == 'sender' ? 'sender' : invoiceData.user_type == 'reciver' ? 'recipient' : '';

        if (!subject) return;

        if (invoiceData.delivery?.[subject]?.phones?.length > 0) {
            let n = invoiceData.delivery?.[subject]?.phones[0]?.number;
            let shouldClear = false;

            if (invoiceData.cityToLabel?.startsWith("Россия") && !n.startsWith("+7")) {
                shouldClear = true;
            } else if (invoiceData.cityToLabel?.startsWith("Беларусь") && !n.startsWith("+375")) {
                shouldClear = true;
            } else if (invoiceData.cityToLabel?.startsWith("Казахстан") && !n.startsWith("+7")) {
                shouldClear = true;
            } else if (invoiceData.cityToLabel?.startsWith("Киргизия") && !n.startsWith("+996")) {
                shouldClear = true;
            } else if (invoiceData.cityToLabel?.startsWith("Армения") && !n.startsWith("+374")) {
                shouldClear = true;
            }

            if (shouldClear) {
                setInvoiceData(oldData => ({
                    ...oldData,
                    delivery: {
                        ...oldData.delivery,
                        [subject]: {
                            ...oldData.delivery[subject],
                            phones: oldData.delivery[subject].phones.map((phone, idx) => 
                                idx === 0 ? {...phone, number: ""} : phone
                            )
                        }
                    }
                }));
            }
        }

    }, [invoiceData.cityTo]);

    useEffect(() => {
        if (invoiceData?.user_info?.contract_type === 3) {
            setInvoiceData(d => ({...d, payment_on_receipt: false}));
        }
    }, [invoiceData.user_info.contract_type]);

    // Auto-set user_type to 'another' (Третья сторона) for EAEU shipments
    useEffect(() => {
        const isEAEU = shouldApplyEAEULogic(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode);
        const defaults = getEAEUDefaultValues(t);
        
        console.log('EAEU Effect triggered - isEAEU:', isEAEU, 'From:', invoiceData.cityFromCountryCode, 'To:', invoiceData.cityToCountryCode, 'Template:', invoiceData._isApplyingTemplate, 'Voice:', invoiceData._isVoiceInput, 'NeedsCheck:', invoiceData._needsEAEUCheck);
        
        // Handle EAEU defaults for voice input after cities are resolved
        if (isEAEU && invoiceData._isVoiceInput && invoiceData._needsEAEUCheck) {
            console.log('APPLYING EAEU DEFAULTS FOR VOICE INPUT!');
            setInvoiceData(oldData => {
                const newData = {...oldData};
                
                // Set user_type to 'another' for EAEU (override voice input)
                newData.user_type = 'another';
                
                // Set EAEU flags
                newData._eaeuAutoSet = true;
                newData._eaeuUserTypeChanged = false;
                newData._eaeuWarningShown = false;
                
                // Override goods name and article with EAEU defaults (keep other voice data like weight)
                newData.places = newData.places.map((place) => ({
                    ...place,
                    goods: place.goods.map((good) => ({
                        ...good,
                        name: defaults.goodsName,
                        article: defaults.article
                    }))
                }));
                
                // Clear the flags
                delete newData._needsEAEUCheck;
                
                return newData;
            });
        }
        // Don't auto-set if applying template or during voice input (unless needs check)
        else if (isEAEU && !invoiceData._isApplyingTemplate && !invoiceData._isVoiceInput) {
            console.log('SETTING EAEU DEFAULTS!');
            setInvoiceData(oldData => {
                const newData = {...oldData};
                
                // Always set user_type to 'another' for EAEU
                newData.user_type = 'another';
                
                // Always reset flags when entering EAEU mode
                newData._eaeuAutoSet = true;
                newData._eaeuUserTypeChanged = false;
                newData._eaeuWarningShown = false;
                
                // Force set default values for all goods in all places
                newData.places = newData.places.map((place) => ({
                    ...place,
                    goods: place.goods.map((good) => ({
                        ...good,
                        name: defaults.goodsName,
                        article: defaults.article
                    }))
                }));
                
                return newData;
            });
        } else if (!isEAEU && invoiceData._eaeuAutoSet) {
            console.log('RESETTING FROM EAEU MODE');
            // Reset to default when leaving EAEU mode
            setInvoiceData(oldData => {
                const { _eaeuAutoSet, _eaeuUserTypeChanged, _eaeuWarningShown, ...cleanData } = oldData;
                return {
                    ...cleanData,
                    user_type: 'sender'
                };
            });
        }
        
        // Clean up voice input flag after processing
        if (invoiceData._isVoiceInput && !invoiceData._needsEAEUCheck) {
            setTimeout(() => {
                setInvoiceData(currentData => {
                    const { _isVoiceInput, ...cleanData } = currentData;
                    return cleanData;
                });
            }, 500);
        }
    }, [invoiceData.cityFrom, invoiceData.cityTo, invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData._isVoiceInput, invoiceData._needsEAEUCheck]);
    
    return (
        <>
        <EAEUWarningModal 
            open={showEAEUWarning} 
            onClose={() => {
                setShowEAEUWarning(false);
                // Allow dropdown to open after user acknowledges the warning
                setTimeout(() => {
                    setUserTypeDropdownOpen(false);
                }, 100);
            }} 
        />
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            paddingBottom: "10rem"
        }}>
        <DoublePanel
            secondPanelBody={
                <div className='secondPanelBody inputsGroup'>
                    <div className="cityInputWrapper">
                        <span className='cityInputAddon'>
                            <span id='cityIconFrom'></span>
                            <span id='cityArrowFrom'></span>
                        </span>
                        <BasicInputDropdown fluid placeholder={t("city_from_placeholder")}
                            searchBy={(k, l, d) => {return true}}
                            ref={fromCityRef}
                            disabled={!invoiceData.contract_number}
                            error={invoiceData.errors?.cityFrom}
                            loading={false}
                            data={searchQueryFrom.length < 3 ? popularCities : searchedCitiesFrom}
                            onSearch={(q) => setSearchQueryFrom(q)}
                            onChange={(c) => {
                                // Find city data from available lists
                                const cityData = searchQueryFrom.length < 3 
                                    ? popularCities.find((city) => city.value === c)
                                    : searchedCitiesFrom.find((city) => city.value === c);
                                
                                const label = cityData?.label || '';
                                const countryCodeFromList = cityData?.countryCode;
                                
                                console.log('FROM - City selected:', c, 'Label:', label, 'CountryCode from list:', countryCodeFromList);
                                
                                // Update city immediately with countryCode from list
                                setInvoiceData(oldData => ({
                                    ...oldData, 
                                    cityFrom: c,
                                    cityFromLabel: label.split(" ")[0] || '',
                                    cityFromCountryCode: countryCodeFromList
                                }));

                                // Also fetch from API to ensure we have the most accurate data
                                if (invoiceData.contract_number) {
                                    getCityInfo({contract_number: invoiceData.contract_number, code: c}).then((info) => {
                                        if (info && info.countryCode !== undefined) {
                                            console.log('FROM - City info fetched:', c, 'CountryCode from API:', info.countryCode);
                                            
                                            setInvoiceData(oldData => ({
                                                ...oldData,
                                                cityFrom: c,
                                                cityFromLabel: info.label || info.name || '',
                                                cityFromCountryCode: info.countryCode
                                            }));
                                        }
                                    });
                                }
                            }}
                            value={invoiceData.cityFrom}/>
                    </div>
                    <div className="cityInputWrapper">
                        <span className='cityInputAddon'>
                            <span id='cityIconTo'></span>
                        </span>
                        <BasicInputDropdown fluid placeholder={t("city_to_placeholder")}
                            searchBy={(k, l, d) => {return true}}
                            ref={toCityRef}
                            disabled={!invoiceData.contract_number}
                            error={invoiceData.errors?.cityTo}
                            loading={false}
                            data={searchQueryTo.length < 3 ? popularCities : searchedCitiesTo}
                            onSearch={(q) => setSearchQueryTo(q)}
                            onChange={(c) => {
                                // Find city data from available lists
                                const cityData = searchQueryTo.length < 3 
                                    ? popularCities.find((city) => city.value === c)
                                    : searchedCitiesTo.find((city) => city.value === c);
                                
                                const label = cityData?.label || '';
                                const countryCodeFromList = cityData?.countryCode;
                                
                                console.log('TO - City selected:', c, 'Label:', label, 'CountryCode from list:', countryCodeFromList);
                                
                                // Update city immediately with countryCode from list
                                setInvoiceData(oldData => ({
                                    ...oldData, 
                                    cityTo: c,
                                    cityToLabel: label.split(" ")[0] || '',
                                    cityToCountryCode: countryCodeFromList
                                }));

                                // Also fetch from API to ensure we have the most accurate data
                                if (invoiceData.contract_number) {
                                    getCityInfo({contract_number: invoiceData.contract_number, code: c}).then((info) => {
                                        if (info && info.countryCode !== undefined) {
                                            console.log('TO - City info fetched:', c, 'CountryCode from API:', info.countryCode);
                                            
                                            setInvoiceData(oldData => ({
                                                ...oldData,
                                                cityTo: c,
                                                cityToLabel: info.label || info.name || '',
                                                cityToCountryCode: info.countryCode
                                            }));
                                        }
                                    });
                                }
                            }}
                            value={invoiceData.cityTo}/>
                    </div>
                    { !isCalculate &&

                    <BasicRadioGroup
                        label={t("payment_on_receipt_label")}
                        disabled={
                            !invoiceData.contract_number ||
                            invoiceData?.user_info?.contract_type === 3 ||
                            !isPaymentOnReceiptAllowed(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData.user_info?.is_kz)
                        }
                        data={[{
                            label: t("yes"),
                            value: true
                        }, {
                            label: t("no"),
                            value: false
                        }
                        ]}
                        style={{width: "10rem"}}
                        onChange={(c) => setInvoiceData(
                            oldData => {
                                const newData = {...oldData};
                                // Block payment on receipt if not allowed
                                const isAllowed = isPaymentOnReceiptAllowed(oldData.cityFromCountryCode, oldData.cityToCountryCode, oldData.user_info?.is_kz);

                                if (!isAllowed) {
                                    newData.payment_on_receipt = false;
                                } else {
                                    newData.payment_on_receipt = c;
                                }

                                return newData;
                            }
                        )}
                        value={invoiceData.payment_on_receipt}
                    />
                    }
                </div>
            }
            secondPanelHeader={
                <div className='secondPanelHeader'>
                    <Icon as={() => SECOND_PANEL_HEADER_SVG} style={{flexGrow: 1}} />
                    <span className='secondPanelHeaderTitle paragraph-16M' style={{flexGrow: 3}}>
                        {t("main_section")}
                    </span>
                </div>
            }
        >
            <div className='inputsGroup'>
                { !isTemplateUpdate &&
                <BasicDropdown label={t("contract_number")} placeholder={t("select_contract_number")}
                    error={invoiceData.errors?.contract_number}
                    data={contracts} onChange={(c) => setInvoiceData({...invoiceData, contract_number: c})}
                    value={invoiceData.contract_number}/>
                }
                { !isCalculate &&
                <>
                    {
                        isTemplate ?
                        <BasicInput placeholder={t("template_name")}
                            flexible
                            disabled={!invoiceData.contract_number}
                            error={invoiceData.errors?.template_name}
                            onChange={(value) => {
                                setInvoiceData(invoiceData => {
                                    // Check if template name already exists with different ID
                                    // During edit: allow same name only if it's the same template (same ID from URL)
                                    const currentTemplate = templates.find((template) => template.id === Number(template_id));
                                    const duplicateTemplate = templates.find((template) => template.template_name === value);

                                    const isDuplicate = () =>{
                                        if (isTemplateUpdate && duplicateTemplate && currentTemplate && duplicateTemplate?.id === currentTemplate?.id) return false;
                                        return !!duplicateTemplate
                                    }

                                    const newErrors = {...invoiceData.errors};
                                    
                                    if (isDuplicate()) {
                                        newErrors.template_name = t("template_name_exists");
                                    } else {
                                        // Clear the duplicate error if it was previously set and name is now unique
                                        if (newErrors.template_name === t("template_name_exists")) {
                                            newErrors.template_name = null;
                                        }
                                    }
                                    
                                    return {
                                        ...invoiceData, 
                                        template_name: value,
                                        errors: newErrors
                                    };
                                });
                            }}
                            value={invoiceData.template_name}/>
                        :
                        <BasicDropdown label={t("template")} placeholder={t("no_template")}
                            disabled={!invoiceData.contract_number}
                            data={[
                                {label: t("no_template"), value: -1},
                                ...templates.map((template) => ({label: cutText(template.template_name, 20), value: template.id}))
                            ]}
                            onChange={(c) => setInvoiceData({...invoiceData, template_id: c})}
                            value={invoiceData.template_id}/>
                    }
                
                    <BasicDropdown 
                        label={t("who_are_you")} 
                        placeholder={t("sender")} 
                        data={getUserTypeValues(t)}
                        disabled={!invoiceData.contract_number}
                        error={invoiceData.errors?.user_type}
                        open={userTypeDropdownOpen}
                        onOpen={() => {
                            const isEAEU = shouldApplyEAEULogic(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode);
                            
                            // Show warning on open if in EAEU mode and user_type is 'another' and hasn't been changed yet
                            if (isEAEU && invoiceData.user_type === 'another' && !invoiceData._eaeuUserTypeChanged && !invoiceData._eaeuWarningShown) {
                                // Don't open dropdown, show warning instead
                                setUserTypeDropdownOpen(false);
                                setShowEAEUWarning(true);
                                setInvoiceData(oldData => ({ ...oldData, _eaeuWarningShown: true }));
                            } else {
                                setUserTypeDropdownOpen(true);
                            }
                        }}
                        onClose={() => {
                            setUserTypeDropdownOpen(false);
                        }}
                        onChange={(c) => {
                            const isEAEU = shouldApplyEAEULogic(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode);
                            
                            // Show warning if user tries to change from 'another' in EAEU mode and hasn't changed it before
                            if (isEAEU && c !== 'another' && !invoiceData._eaeuUserTypeChanged && !invoiceData._eaeuWarningShown) {
                                setShowEAEUWarning(true);
                                setInvoiceData(oldData => ({ ...oldData, _eaeuWarningShown: true, _eaeuUserTypeChanged: true }));
                                return; // Don't change the value
                            }

                            // Helper function to deep copy a delivery subject
                            const deepCopySubject = (subj) => ({
                                fio: subj?.fio || '',
                                contract_face: subj?.contract_face || '',
                                phones: (subj?.phones || []).map(p => ({...p}))
                            });
                            
                            // When switching between sender and receiver, swap their data
                            // When switching to "another", clear all delivery data
                            if (c == "reciver" || c == "sender") {
                                // Deep copy both subjects before swapping
                                const senderCopy = deepCopySubject(invoiceData.delivery?.sender);
                                const recipientCopy = deepCopySubject(invoiceData.delivery?.recipient);
                                
                                setInvoiceData({
                                    ...invoiceData,
                                    user_type: c,
                                    delivery: {
                                        sender: recipientCopy,
                                        recipient: senderCopy
                                    },
                                    _eaeuUserTypeChanged: true
                                })
                            } else {
                                // Switching to "another" (third party): clear all delivery data with fresh objects
                                setInvoiceData({
                                    ...invoiceData,
                                    user_type: c,
                                    delivery: {
                                        sender: {
                                            fio: '',
                                            contract_face: '',
                                            phones: [{ number: '', additional: '' }]
                                        },
                                        recipient: {
                                            fio: '',
                                            contract_face: '',
                                            phones: [{ number: '', additional: '' }]
                                        }
                                    },
                                    _eaeuUserTypeChanged: true
                                })
                            }
                        }}
                        value={invoiceData.user_type}/>
                </>
                }
            </div>
        </DoublePanel>

        <PlacesPanel isCalculate={isCalculate} userInfo={invoiceData.user_info} />
        </div>
        </>
    )
}
